import {
	Extension,
	ExtensionProperty,
	type IProperty,
	PropertyType,
	type ReaderContext,
	type WriterContext,
} from "@gltf-transform/core";
import { ResultAsync } from "neverthrow";
import type { Group, Material, Mesh } from "three";
import { Texture } from "three";
import type { GLTFParser } from "three/addons/loaders/GLTFLoader.js";

interface PatchedGLTFParser extends GLTFParser {
	textureCache: Record<string, unknown>;
	sourceCache: Record<number, unknown>;
	cache: {
		remove: (key: string) => void;
		get: (key: string) => Promise<unknown>;
	};
}

export const EXTENSION_NAME = "DPL_image_progressive";

const isMobile =
	/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
		navigator.userAgent,
	);

function getImageURIMimeType(uri: string) {
	if (
		uri.search(/\.jpe?g($|\?)/i) > 0 ||
		uri.search(/^data:image\/jpeg/) === 0
	) {
		return "image/jpeg";
	}

	if (
		uri.search(/\.webp($|\?)/i) > 0 ||
		uri.search(/^data:image\/webp/) === 0
	) {
		return "image/webp";
	}

	if (
		uri.search(/\.ktx2($|\?)/i) > 0 ||
		uri.search(/^data:image\/ktx2/) === 0
	) {
		return "image/ktx2";
	}

	return "image/png";
}

export function upscalePendingTextures(parser: GLTFParser, scene: Group) {
	const startTime = Date.now();

	const queue: (() => Promise<void>)[] = [];

	scene.traverse(async (obj) => {
		if (!(obj as Mesh).isMesh) {
			return;
		}

		const mesh = obj as Mesh;

		const material = mesh.material as Material;

		if (!material) {
			return;
		}

		if (material.userData.upscaled) {
			return;
		}

		const materialIndex = parser.associations.get(material)?.materials ?? -1;

		if (materialIndex === -1) {
			return;
		}

		let needsUpdate = false;
		for (const value of Object.values(material)) {
			if (!(value instanceof Texture)) {
				continue;
			}

			const texture = value;

			const textureIndex = parser.associations.get(texture)?.textures ?? -1;

			if (textureIndex === -1) {
				continue;
			}

			const textureDef = parser.json.textures[textureIndex];

			const imageIndex =
				textureDef.source ?? textureDef.extensions?.KHR_texture_basisu.source;

			const imageDef = parser.json.images[imageIndex];
			if (!imageDef.extensions?.[EXTENSION_NAME]) {
				continue;
			}

			const { desktop, mobile } = imageDef.extensions[EXTENSION_NAME];

			imageDef.uri = isMobile ? mobile : desktop;
			imageDef.mimeType = getImageURIMimeType(imageDef.uri);

			if (imageDef.mimeType === "image/ktx2") {
				textureDef.extensions ??= {};
				textureDef.extensions.KHR_texture_basisu = {
					source: imageIndex,
				};
			} else {
				textureDef.source = imageIndex;
				delete textureDef.extensions?.KHR_texture_basisu;
			}

			delete (parser as PatchedGLTFParser).textureCache[
				`${imageDef.uri}:${textureDef.sampler}`
			];
			delete (parser as PatchedGLTFParser).sourceCache[imageIndex];
			(parser as PatchedGLTFParser).cache.remove(`texture:${textureIndex}`);

			needsUpdate = true;
		}

		if (needsUpdate) {
			queue.push(async () => {
				const original = mesh.material as Material;

				const cachedMaterial = (await (parser as PatchedGLTFParser).cache.get(
					`material:${materialIndex}`,
				)) as Material;

				if (cachedMaterial?.userData?.upscaled) {
					mesh.material = cachedMaterial;
					parser.assignFinalMaterial(mesh);
					return;
				}

				(parser as PatchedGLTFParser).cache.remove(`material:${materialIndex}`);

				const material = await parser.getDependency("material", materialIndex);

				mesh.material = material;
				material.userData.upscaled = true;
				parser.assignFinalMaterial(mesh);

				(original as Material)?.dispose();
			});
		}
	});

	return ResultAsync.combine(
		queue.map((task) => ResultAsync.fromPromise(task(), (err) => err as Error)),
	).map(() => ({
		startTime,
		texturesUpscaled: queue.length,
	}));
}

export interface IProgressive extends IProperty {
	initial: string | null;
	desktop: string | null;
	mobile: string | null;
}

export class Progressive extends ExtensionProperty<IProgressive> {
	static EXTENSION_NAME = EXTENSION_NAME;
	extensionName = EXTENSION_NAME;
	propertyType = "Progressive";
	parentTypes = [PropertyType.TEXTURE];

	protected init() {
		this.extensionName = EXTENSION_NAME;
		this.propertyType = "Progressive";
		this.parentTypes = [PropertyType.NODE];
	}

	setInitial(initial: string | null) {
		return this.set("initial", initial);
	}

	getInitial() {
		return this.get("initial");
	}

	setDesktop(desktop: string | null) {
		return this.set("desktop", desktop);
	}

	getDesktop() {
		return this.get("desktop");
	}

	setMobile(mobile: string | null) {
		return this.set("mobile", mobile);
	}

	getMobile() {
		return this.get("mobile");
	}
}

export class DPLImageProgressive extends Extension {
	static EXTENSION_NAME = EXTENSION_NAME;
	extensionName = EXTENSION_NAME;

	createProgressive() {
		return new Progressive(this.document.getGraph());
	}

	read(context: ReaderContext): this {
		for (const [
			imageIndex,
			imageDef,
		] of context.jsonDoc.json.images?.entries() || []) {
			const extDef = imageDef.extensions?.[EXTENSION_NAME] as {
				initial: string;
				desktop: string;
				mobile: string;
			};

			if (!extDef) {
				continue;
			}

			const progressive = this.createProgressive();

			context.textures[imageIndex].setExtension(EXTENSION_NAME, progressive);

			progressive.setInitial(extDef.initial);
			progressive.setDesktop(extDef.desktop);
			progressive.setMobile(extDef.mobile);
		}

		return this;
	}

	write(context: WriterContext): this {
		for (const image of this.document.getRoot().listTextures()) {
			const progressive = image.getExtension<Progressive>(EXTENSION_NAME);

			if (progressive) {
				const imageIndex = context.imageIndexMap.get(image);
				if (imageIndex === undefined) {
					continue;
				}

				const imageDef = context.jsonDoc.json.images?.[imageIndex];
				if (!imageDef) {
					continue;
				}

				imageDef.extensions ??= {};
				imageDef.extensions[EXTENSION_NAME] = {
					initial: progressive.getInitial(),
					desktop: progressive.getDesktop(),
					mobile: progressive.getMobile(),
				};
			}
		}

		return this;
	}
}
